import { IconComponent, Notepad2 } from '@/components/icons';
import { TooltipProvider } from '@/components/ui/tooltip';
import { getSourceMessage, getSourceRowId, ResolvedSource } from './ai-data-sources';
import { GateAction, GateCount, GateNotice } from './AIPanelGate';
import type { PaidEntitlementPlan } from '@/api/entitlements';

type AvailablePlan = {
    id: PaidEntitlementPlan;
    name: string;
};

type SourceAction = {
    /** What to do while the source is missing — a verb and its noun. */
    label: string;
    /**
     * What to do once a `document` source is in place. Like `label`, it is the
     * whole sentence on a full-width button, so it carries the verb and the
     * noun. Falls back to `label`.
     */
    manageLabel?: string;
    /**
     * Label to use once the source has failed, when the route out is the same
     * one but the wording should read as a recovery rather than as management.
     * Lives on the action so the copy stays with the step that owns it.
     */
    failedLabel?: string;
    onClick: () => void;
    disabled?: boolean;
    disabledTooltip?: string;
    unavailablePlan?: AvailablePlan | null;
};

export type SourceActionsMap = Record<string, SourceAction>;

/** Leading glyph per source. Only documents get one; the rest are routes. */
const SOURCE_ICON: Record<string, IconComponent> = {
    anamnesis: Notepad2,
};

interface AIDataSourcesProps {
    sources: ResolvedSource[];
    /** Section this body belongs to — keys the row ids the CTA points at. */
    stepId: string;
    actions?: SourceActionsMap;
}

/**
 * The body of the AI panel: what is in the way, and the way through it.
 *
 * It used to be a checklist of every source the section reads, satisfied ones
 * included, each row carrying a coloured dot, a status phrase and a link. That
 * made the reader decode six rows to find the one or two that mattered. Now
 * only what is in the way is drawn, and each blocker is the button that clears
 * it — so the answer to "what am I missing?" is also the answer to "what do I
 * press?".
 *
 * The one thing that survives being satisfied is a `document`. The anamnesis is
 * an object the psychologist owns and keeps changing, and this panel is the
 * only route to it from inside the report, so it holds its place instead of
 * vanishing the moment it lands.
 */
export default function AIDataSources({ sources, stepId, actions = {} }: AIDataSourcesProps) {
    // Nothing is re-sorted here. The order is the order the backend declares,
    // which is stable for a section, so a button never moves out from under the
    // cursor at the exact moment its state changes — which is when the user is
    // reaching for it.
    const pending = sources.filter((source) => source.blocking);
    const failed = sources.filter((source) => source.availability === 'failed');
    const processing = sources.filter((source) => source.availability === 'processing');
    // Documents that are no longer in the way, split by whether the file is
    // actually there. On História Clínica a source goes `available` once the
    // derived history is ready, which survives the anamnesis being deleted — so
    // "not blocking" and "on file" are genuinely different questions, and the
    // panel answers them with different rows.
    //
    // `present !== false` rather than `=== true`: while the lookup is in flight
    // the document row shows, because one that is almost certainly there should
    // not blink out of the panel on every step change.
    const settledDocuments = sources.filter(
        (source) => source.kind === 'document' && source.availability === 'available',
    );
    const documentsOnFile = settledDocuments.filter((source) => source.present !== false);
    const documentsMissing = settledDocuments.filter((source) => source.present === false);

    if (
        pending.length === 0 &&
        failed.length === 0 &&
        processing.length === 0 &&
        settledDocuments.length === 0
    ) {
        return null;
    }

    return (
        <TooltipProvider delayDuration={0}>
            <div className="flex flex-col gap-2.5">
                {/* Failures first: they are the only state reporting that
                    something went wrong, and the recovery is usually the same
                    route the blocker below would offer. */}
                {failed.map((source) => {
                    const action = actions[source.id];
                    const rowId = getSourceRowId(stepId, source.id);
                    const message = getSourceMessage(source);

                    return (
                        <div key={source.id} className="flex flex-col gap-2.5">
                            <GateNotice tone="failure" id={rowId}>
                                {message ?? `Não foi possível preparar: ${source.label}.`}
                            </GateNotice>
                            {action ? (
                                <GateAction
                                    label={action.failedLabel ?? action.label}
                                    onClick={action.onClick}
                                    icon={SOURCE_ICON[source.id]}
                                    disabled={action.disabled}
                                    disabledTooltip={action.disabledTooltip}
                                    unavailablePlan={action.unavailablePlan}
                                    describedBy={rowId}
                                />
                            ) : null}
                        </div>
                    );
                })}

                {processing.map((source) => (
                    <GateNotice key={source.id} id={getSourceRowId(stepId, source.id)}>
                        {getSourceMessage(source) ?? `Preparando: ${source.label}.`}
                    </GateNotice>
                ))}

                {pending.length > 0 && (
                    <div className="flex flex-col gap-2.5">
                        <GateCount count={pending.length} />
                        {pending.map((source) => {
                            const action = actions[source.id];
                            const rowId = getSourceRowId(stepId, source.id);

                            // A blocker with no route is still worth naming —
                            // silence would leave the count pointing at
                            // nothing — but it cannot pretend to be a button.
                            if (!action) {
                                return (
                                    <p
                                        key={source.id}
                                        id={rowId}
                                        className="text-[13px] leading-snug text-foreground"
                                    >
                                        {source.label}
                                    </p>
                                );
                            }

                            return (
                                <GateAction
                                    key={source.id}
                                    id={rowId}
                                    label={action.label}
                                    onClick={action.onClick}
                                    // Only a document gets a glyph. A generic
                                    // "+" on "Escrever a Conclusão" claims you
                                    // are adding something when you are being
                                    // sent to another section to write it.
                                    icon={SOURCE_ICON[source.id]}
                                    disabled={action.disabled}
                                    disabledTooltip={action.disabledTooltip}
                                    unavailablePlan={action.unavailablePlan}
                                />
                            );
                        })}
                    </div>
                )}

                {documentsOnFile.map((source) => {
                    const action = actions[source.id];

                    if (!action) return null;

                    return (
                        <GateAction
                            key={source.id}
                            id={getSourceRowId(stepId, source.id)}
                            label={action.manageLabel ?? action.label}
                            onClick={action.onClick}
                            // No glyph here, unlike the missing-document row
                            // above: the mark says "a document goes in", and
                            // one already did.
                            disabled={action.disabled}
                            disabledTooltip={action.disabledTooltip}
                            unavailablePlan={action.unavailablePlan}
                        />
                    );
                })}

                {/* Nothing on file, and nothing being held up either: the
                    section can generate from what it already derived. So the
                    route stays — this panel is the only way to the anamnesis
                    from inside the report — but it sits outside the count,
                    because counting it would say the button is blocked when it
                    is not. */}
                {documentsMissing.map((source) => {
                    const action = actions[source.id];

                    if (!action) return null;

                    return (
                        <GateAction
                            key={source.id}
                            id={getSourceRowId(stepId, source.id)}
                            label={action.label}
                            onClick={action.onClick}
                            icon={SOURCE_ICON[source.id]}
                            disabled={action.disabled}
                            disabledTooltip={action.disabledTooltip}
                            unavailablePlan={action.unavailablePlan}
                        />
                    );
                })}
            </div>
        </TooltipProvider>
    );
}
